import {
  CompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { CatalogApi } from '@backstage/plugin-catalog-react';

type Entity = Awaited<ReturnType<CatalogApi['getEntityByRef']>>;

const CACHE_TTL_MS = 5 * 60 * 1000;
const BATCH_DELAY_MS = 20;
const MAX_BATCH_SIZE = 100;

// SimpleIcons only reads annotations; keep the payload small.
const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.annotations',
];

/**
 * Wraps a CatalogApi so `getEntityByRef` calls are de-duplicated, cached for a
 * few minutes and coalesced into batched `getEntitiesByRefs` requests.
 *
 * SimpleIconsEntityPresentationApi calls `getEntityByRef` once per rendered
 * entity ref link, so a catalog page with owner/system columns fires one
 * `by-name` request per cell, again on every kind switch or re-mount.
 */
export function createBatchedCatalogApi(catalogApi: CatalogApi): CatalogApi {
  const cache = new Map<string, { at: number; result: Promise<Entity> }>();
  let queue: {
    ref: string;
    resolve: (e: Entity) => void;
    reject: (err: unknown) => void;
  }[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = async () => {
    timer = undefined;
    const pending = queue;
    queue = [];
    for (let i = 0; i < pending.length; i += MAX_BATCH_SIZE) {
      const chunk = pending.slice(i, i + MAX_BATCH_SIZE);
      try {
        const { items } = await catalogApi.getEntitiesByRefs({
          entityRefs: chunk.map(p => p.ref),
          fields: FIELDS,
        });
        chunk.forEach((p, index) => p.resolve(items[index] ?? undefined));
      } catch (err) {
        chunk.forEach(p => p.reject(err));
      }
    }
  };

  const getEntityByRef = (ref: string | CompoundEntityRef): Promise<Entity> => {
    let key: string;
    try {
      key = stringifyEntityRef(
        typeof ref === 'string' ? parseEntityRef(ref) : ref,
      ).toLocaleLowerCase('en-US');
    } catch (err) {
      return Promise.reject(err);
    }

    const cached = cache.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      return cached.result;
    }

    const result = new Promise<Entity>((resolve, reject) => {
      queue.push({ ref: key, resolve, reject });
      if (!timer) {
        timer = setTimeout(flush, BATCH_DELAY_MS);
      }
    });
    // Don't cache failures, so a later render can retry.
    result.catch(() => cache.delete(key));
    cache.set(key, { at: Date.now(), result });
    return result;
  };

  const batched = Object.create(catalogApi) as CatalogApi;
  batched.getEntityByRef = getEntityByRef as CatalogApi['getEntityByRef'];
  return batched;
}
